import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Pagination,
  Button,
  Stack
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Timer as TimerIcon,
  Score as ScoreIcon
} from '@mui/icons-material';
import quizService from '../../services/quizService';
import { useAuth } from '../../contexts/AuthContext';

interface QuizAttempt {
  id: number;
  user_id?: number;
  user?: {
    id: number;
    name: string;
    email: string;
  };
  user_info?: {
    name?: string;
    email?: string;
  };
  user_name?: string; // Computed from accessor
  user_email?: string; // Computed from accessor
  score: number;
  total_score: number;
  percentage: number;
  time_taken: number; // in seconds
  started_at: string;
  submitted_at: string;
  ip_address?: string;
}

interface QuizAttemptsData {
  data: QuizAttempt[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

interface QuizStats {
  total_attempts: number;
  average_score: number;
  highest_score: number;
  lowest_score: number;
  average_time: number; // in minutes
}

interface QuizInfo {
  id: number;
  title: string;
  description?: string;
  created_at: string;
}

const QuizAttempts: React.FC = () => {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [attempts, setAttempts] = useState<QuizAttemptsData | null>(null);
  const [stats, setStats] = useState<QuizStats | null>(null);
  const [quiz, setQuiz] = useState<QuizInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Check permissions - will be checked by API, but redirect early if no user
  useEffect(() => {
    if (!user) {
      navigate('/quizzes');
      return;
    }
  }, [user, navigate]);

  // Fetch quiz attempts
  const fetchAttempts = useCallback(async (page: number = 1, search: string = '') => {
    if (!quizId) return;

    try {
      setLoading(true);
      const response = await quizService.getQuizAttempts(parseInt(quizId), {
        page,
        search: search.trim() || undefined,
        per_page: 20
      });

      setAttempts(response.data);
      setStats(response.stats);
      setQuiz(response.quiz);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching quiz attempts:', err);
      setError(err.message || 'Không thể tải danh sách người làm bài');
    } finally {
      setLoading(false);
    }
  }, [quizId]);

  useEffect(() => {
    if (quizId) {
      fetchAttempts(currentPage, searchTerm);
    }
  }, [quizId, currentPage, searchTerm, fetchAttempts]);

  // Handle search
  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    setCurrentPage(1);
    fetchAttempts(1, searchTerm);
  };

  // Format time taken
  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${remainingSeconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${remainingSeconds}s`;
    }
  };

  // Format date
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString('vi-VN');
  };

  // Get score color
  const getScoreColor = (percentage: number): 'success' | 'warning' | 'error' => {
    if (percentage >= 80) return 'success';
    if (percentage >= 60) return 'warning';
    return 'error';
  };

  // Get user display name
  const getUserDisplayName = (attempt: QuizAttempt): string => {
    // Use computed user_name from API if available
    if (attempt.user_name) {
      return attempt.user_name;
    }
    // Fallback to original logic
    if (attempt.user) {
      return attempt.user.name;
    }
    if (attempt.user_info?.name) {
      return attempt.user_info.name;
    }
    return 'Khách';
  };

  // Get user email
  const getUserEmail = (attempt: QuizAttempt): string => {
    // Use computed user_email from API if available
    if (attempt.user_email) {
      return attempt.user_email;
    }
    // Fallback to original logic
    if (attempt.user) {
      return attempt.user.email;
    }
    if (attempt.user_info?.email) {
      return attempt.user_info.email;
    }
    return 'Không có email';
  };

  if (loading && !attempts) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/quizzes')}
        >
          Quay lại danh sách Quiz
        </Button>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box mb={3}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/quizzes')}
          sx={{ mb: 2 }}
        >
          Quay lại
        </Button>

        <Typography variant="h4" gutterBottom>
          Danh sách người làm bài
        </Typography>

        {quiz && (
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {quiz.title}
          </Typography>
        )}
      </Box>

      {/* Statistics */}
      {stats && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 3, mb: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary">
                {stats.total_attempts}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tổng lượt làm
              </Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">
                {stats.average_score}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Điểm trung bình
              </Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">
                {stats.highest_score}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Điểm cao nhất
              </Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="error.main">
                {stats.lowest_score}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Điểm thấp nhất
              </Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="info.main">
                {stats.average_time}m
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Thời gian TB
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Search */}
      <Box component="form" onSubmit={handleSearch} sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Tìm kiếm theo tên hoặc email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 400 }}
        />
      </Box>

      {/* Results Table */}
      <Card>
        <CardContent>
          {attempts && attempts.data.length > 0 ? (
            <>
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Người làm bài</TableCell>
                      <TableCell align="center">Điểm số</TableCell>
                      <TableCell align="center">Thời gian làm</TableCell>
                      <TableCell align="center">Ngày làm</TableCell>
                      <TableCell align="center">Loại tài khoản</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {attempts.data.map((attempt) => (
                      <TableRow key={attempt.id} hover>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <PersonIcon fontSize="small" color="action" />
                              <Typography variant="body2" fontWeight="medium">
                                {getUserDisplayName(attempt)}
                              </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={1}>
                              <EmailIcon fontSize="small" color="action" />
                              <Typography variant="caption" color="text.secondary">
                                {getUserEmail(attempt)}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>

                        <TableCell align="center">
                          <Stack spacing={0.5} alignItems="center">
                            <Chip
                              label={`${attempt.percentage}%`}
                              color={getScoreColor(attempt.percentage)}
                              size="small"
                            />
                            <Typography variant="caption" color="text.secondary">
                              {attempt.score}/{attempt.total_score}
                            </Typography>
                          </Stack>
                        </TableCell>

                        <TableCell align="center">
                          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                            <TimerIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              {formatTime(attempt.time_taken)}
                            </Typography>
                          </Box>
                        </TableCell>

                        <TableCell align="center">
                          <Typography variant="body2">
                            {formatDate(attempt.submitted_at)}
                          </Typography>
                        </TableCell>

                        <TableCell align="center">
                          <Chip
                            label={attempt.user ? 'Đã đăng ký' : 'Khách'}
                            variant={attempt.user ? 'filled' : 'outlined'}
                            color={attempt.user ? 'primary' : 'default'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {attempts.last_page > 1 && (
                <Box display="flex" justifyContent="center" mt={3}>
                  <Pagination
                    count={attempts.last_page}
                    page={currentPage}
                    onChange={(_, page) => setCurrentPage(page)}
                    color="primary"
                  />
                </Box>
              )}
            </>
          ) : (
            <Box textAlign="center" py={6}>
              <ScoreIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Chưa có ai làm bài quiz này
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Chia sẻ link quiz để mọi người có thể tham gia làm bài
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default QuizAttempts; 